// Paramount: Custom options web component
/**
 * @typedef {Object} SelectedValue
 * @property {string} id - Unique identifier for the selected option
 * @property {string} label - Display label for the option group
 * @property {string} value - The selected value
 * @property {string} name - Metaobject name used for bundle price adjustment matching
 * @property {string} option - Raw option text used for bundle price adjustment matching
 * @property {string[]} variantIds - Array of associated variant IDs
 * @property {string} selectionType - Type of selection (single/multiple)
 * @property {number} price - Additional price for this option in cents
 */

/**
 * @typedef {Object} BundlePriceAdjustment
 * @property {string[]|string} requiredValues - Metaobject names that must match the bundle selection
 * @property {number} priceDecrease - Percentage price decrease when required values match
 */

/**
 * Custom options web component for product customization.
 * Manages custom product options including selects and checkboxes with dynamic pricing.
 * @extends HTMLElement
 * 
 * @property {SelectedValue[]} selectedValues - Array of currently selected option values
 * @property {NodeListOf<CustomSelect>} customSelects - All custom select elements
 * @property {NodeListOf<HTMLInputElement>} options - All option input elements
 * @property {NodeListOf<HTMLElement>} optionsToSelect - Options that should be pre-selected
 * @property {NodeListOf<HTMLInputElement>} optionsToCheck - Checkboxes that should be pre-checked
 * @property {number} optionsCount - Total number of option groups
 * @property {HTMLElement} productForm - Parent product form element
 * @property {HTMLElement} productInfo - Parent product info element
 * @property {NodeListOf<HTMLSelectElement>} dropdownSelects - Standard variant dropdown selects
 * @property {HTMLInputElement} variantInput - Hidden input for selected variant ID
 * @property {string|null} customOptionVariantId - ID of the custom option variant
 * @property {HTMLInputElement} firstAvailableVariantInput - Input containing first available variant
 * @property {string} firstAvailableVariantId - ID of first available variant
 * @property {HTMLInputElement} standardOptionsUnavailable - Input indicating standard options unavailable
 * @property {HTMLButtonElement} addToCartButton - Add to cart button element
 * @property {HTMLButtonElement} selectOptionsButton - Select options prompt button
 * @property {NodeListOf<HTMLInputElement>} optionsTabs - Radio inputs for switching between option types
 * @property {HTMLElement} optionsTabsWrapper - Wrapper element for options tabs
 * @property {HTMLElement|null} stockEl - Stock display element
 * @property {NodeListOf<HTMLElement>} prices - Price display elements
 * @property {NodeListOf<HTMLElement>} compareAtPrices - Compare-at price display elements
 * @property {string} previousPrice - Previously displayed price text
 * @property {string} previousCompareAtPrice - Previously displayed compare-at price text
 * @property {number} productPrice - Base product price in cents
 * @property {number} productCompareAtPrice - Base product compare-at price in cents
 * @property {number} addonsPrice - Total price of selected addons in cents
 * @property {HTMLInputElement} acceptTermsInput - Terms acceptance checkbox
 * @property {HTMLElement} acceptTermsError - Terms error message element
 * @property {boolean} customSelected - Whether custom options are currently selected
 * @property {string[]} firstAvailableVariantOptions - Array of first available variant option values
 * @property {boolean} isBundleProduct - Whether this product uses bundle custom options
 * @property {string|null} bundleId - Shared bundle ID applied to charge line items
 * @property {CustomCheckboxes|null} bundleOption - The set_as_bundle checkbox group
 * @property {BundlePriceAdjustment[]} priceAdjustments - Bundle price adjustment rules
 */
class CustomOptions extends HTMLElement {
    constructor() {
      super();

      this.productForm = this.closest('product-form');
      this.optionsTabsWrapper = this.closest('.options-tabs');
      this.customOptionVariantId = this.productForm.querySelector('input#custom_option_variant')?.value || null;

      if(this.customOptionVariantId == null){
        console.warn('No custom option variant ID found');
        this.optionsTabsWrapper.classList.add('no-custom-option-variant');
        return;
      }

      this.addonsPrice = 0;
  
      this.selectedValues = [];
      this.customSelects = this.querySelectorAll('custom-select');
      this.options = this.querySelectorAll('.custom-select-input, .custom-checkboxes-input');
      this.optionsToSelect = this.querySelectorAll('.custom-select-option[data-selected]:not(.hidden-option)');
      this.optionsToCheck = this.querySelectorAll('.custom-checkboxes-option:not(.hidden-option) .custom-checkbox-input[data-check]');
      this.optionsCount = Number(this.dataset.optionsCount);
      this.productInfo = this.closest('product-info');
      this.dropdownSelects = this.productForm.querySelectorAll('.dropdown-select');
      this.variantInput = this.productForm.querySelector('input[name="id"]');
      this.firstAvailableVariantInput = this.productForm.querySelector('input#first_available_selected_variant');
      this.firstAvailableVariantId = this.firstAvailableVariantInput.value;
      this.standardOptionsUnavailable = this.productForm.querySelector('input[name="standard_options_unavailable"]');
      this.addToCartButton = this.productForm.querySelector('.product-form__submit');
      this.selectOptionsButton = this.productForm.querySelector('.product-select-options');
      this.optionsTabs = this.productForm.querySelectorAll('input[name="options_tabs"]');
      this.stockEl = this.productForm.querySelector('.dflex_orderdeadline_stock:not(.hidden)');
      this.prices = this.productInfo.querySelectorAll('.js-price-regular');
      this.compareAtPrices = this.productInfo.querySelectorAll('.js-price-compare-at-price');
      this.previousPrice = this.prices[0]?.textContent.trim();
      this.previousCompareAtPrice = this.compareAtPrices[0]?.textContent.trim();
      this.productPrice = Number(this.dataset.productPrice);
      this.productCompareAtPrice = this.dataset.productCompareAtPrice ? Number(this.dataset.productCompareAtPrice) : 0;
      this.acceptTermsInput = this.productForm.querySelector('input[name="accept_custom_terms"]');
      this.acceptTermsError = this.querySelector('.custom-terms-error-message');
      this.bundleId = this.dataset.bundleId || null;
      this.isBundleProduct = Boolean(this.bundleId);
      this.bundleOption = this.querySelector('custom-checkboxes[data-set-as-bundle]');
      this.priceAdjustments = this.parseBundlePriceAdjustments();

      this.customSelected = this.optionsTabs[1]?.checked == true;

      if (this.customSelected) {
        this.initCustomOptions();
      } else {
        this.setCustomOptionsVisible(false);
        this.setTermsRequired(false);
      }
    }

    /**
     * Lifecycle method called when element is connected to the DOM.
     * Sets up event listeners and initializes pre-selected options.
     * @returns {void}
     */
    connectedCallback() {
      this.options.forEach(option => {
        option.addEventListener('change', this.onOptionChange.bind(this));
      });
  
      this.optionsTabs.forEach(tab => {
        tab.addEventListener('change', this.onOptionsTabChange.bind(this));
      });
  
      this.customSelects.forEach(customSelect => {
        customSelect.addEventListener('click', this.onCustomSelectClick.bind(this));
      });

      this.addToCartButton.addEventListener('click', this.onAddToCartClick.bind(this));

      try {
        this.firstAvailableVariantOptions = JSON.parse(this.firstAvailableVariantInput.dataset.options || '[]');
      } catch (error) {
        console.error('Error parsing custom option variant options', error);
      }

      customElements.whenDefined('custom-select').then(() => {
        this.optionsToSelect.forEach(option => option.click());
      });

      customElements.whenDefined('custom-checkboxes').then(() => {
        this.optionsToCheck.forEach(option => {
          option.checked = true;
          option.dispatchEvent(new Event('change', { bubbles: true }));
        });

        if (this.isBundleProduct) {
          this.ensureBundleSelection();
        }
      });
    }

    /**
     * Lifecycle method called when element is disconnected from the DOM.
     * Removes all event listeners to prevent memory leaks.
     * @returns {void}
     */
    disconnectedCallback() {
      this.options?.forEach(option => {
        option.removeEventListener('change', this.onOptionChange);
      });
      
      this.optionsTabs?.forEach(tab => {
        tab.removeEventListener('change', this.onOptionsTabChange);
      });
      
      this.customSelects?.forEach(customSelect => {
        customSelect.removeEventListener('click', this.onCustomSelectClick);
      });

      this.addToCartButton?.removeEventListener('click', this.onAddToCartClick);
    }

    /**
     * Parses bundle price adjustment rules from the embedded JSON script.
     * @returns {BundlePriceAdjustment[]}
     */
    parseBundlePriceAdjustments() {
      const bundleDataScript = this.querySelector('#custom-options-bundle-data');

      if (!bundleDataScript) return [];

      try {
        return JSON.parse(bundleDataScript.textContent);
      } catch (error) {
        console.error('Error parsing custom options bundle data', error);
        return [];
      }
    }

    /**
     * Generates a random bundle ID using the same range/modulo logic as Liquid.
     * Liquid equivalent: "now" | date: "%N" | modulo: diff | plus: min
     * @returns {number}
     */
    generateBundleId() {
      const min = 100;
      const max = 10000;
      const diff = max - min;
      const nanoseconds = Math.floor((Date.now() + performance.now()) * 1e6);

      return (nanoseconds % diff) + min;
    }

    /**
     * Regenerates the bundle ID after a successful add to cart so the next
     * bundle submission gets a unique _bundle_id.
     * @returns {void}
     */
    refreshBundleId() {
      if (!this.isBundleProduct) return;

      this.bundleId = String(this.generateBundleId());
      this.dataset.bundleId = this.bundleId;

      if (this.customSelected) {
        this.productForm.bundleId = this.bundleId;
      }
    }
    
    /**
     * Shows or hides the "custom" option in each variant dropdown.
     * The option is removed from the DOM rather than wrapped in a hidden span,
     * because iOS shows wrapped/hidden options in its native picker.
     * @param {boolean} visible - Whether the custom option should be selectable
     * @returns {void}
     */
    setCustomOptionsVisible(visible) {
      if (!this.customOptionStash) {
        this.customOptionStash = [...this.productInfo.querySelectorAll('select option[value="custom"]')]
          .map(option => ({ option, select: option.closest('select') }));
      }

      this.customOptionStash.forEach(({ option, select }) => {
        if (visible) {
          if (option.parentElement !== select) select.appendChild(option);
        } else {
          option.remove();
        }
      });
    }

    /**
     * Handles add to cart button clicks.
     * Validates terms acceptance if custom options are selected.
     * @param {MouseEvent} event - The click event
     * @returns {void}
     */
    onAddToCartClick(event) {
      if (this.isCustomTabActive() && !this.areCustomOptionsComplete()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.checkAddToCartButtonVisibility();
        return;
      }

      if (this.isCustomTabActive() && this.acceptTermsInput && !this.acceptTermsInput.checked) {
        event.preventDefault();
        this.acceptTermsError.classList.remove('hidden');
      } else {
        this.acceptTermsError?.classList.add('hidden');
      }
    }
  
    /**
     * Handles option tab change events.
     * Switches between custom and standard product options.
     * @param {Event} event - The change event from the tab input
     * @returns {void}
     */
    onOptionsTabChange(event) {
      let tab = event.target;

      tab.classList.contains('custom-options-tab') && tab.checked ? this.initCustomOptions() : this.initStandardOptions();
    }

    /**
     * Initializes custom options mode.
     * Updates form inputs, pricing, and UI to reflect custom option selection.
     * @returns {void}
     */
    initCustomOptions() {
      this.prices = this.productInfo.querySelectorAll('.js-price-regular');
      this.compareAtPrices = this.productInfo.querySelectorAll('.js-price-compare-at-price');

      this.setCustomOptionsVisible(true);

      this.dropdownSelects.forEach(dropdownSelect => {
        dropdownSelect.value = 'custom';
        dropdownSelect.dispatchEvent(new Event('change'));
      });

      if (!this.isBundleProduct) {
        this.variantInput.value = this.customOptionVariantId;
      }

      this.options.forEach(option => {
        const currentName = option.getAttribute('name') || '';
        option.setAttribute('name', currentName.replace(/^ignore-properties\[/, 'properties['));
      });

      this.checkAddToCartButtonVisibility();
      this.setTermsRequired(true);

      if(this.stockEl) this.stockEl.style.display = 'none';

      this.previousPrice = this.prices[0]?.textContent.trim();

      this.previousCompareAtPrice = this.compareAtPrices[0]?.textContent.trim();

      this.customSelected = true;
      this.updateProductFormAddons();
      this.updateDisplayedPrices();
    }

    /**
     * Initializes standard options mode.
     * Resets form to use standard product variants.
     * @returns {void}
     */
    initStandardOptions() {
      this.dropdownSelects.forEach(dropdownSelect => {
        dropdownSelect.value = 'Select';
        dropdownSelect.dispatchEvent(new Event('change'));
      });

      this.setCustomOptionsVisible(false);

      this.variantInput.value = this.firstAvailableVariantId;

      this.options.forEach(option => {
        const currentName = option.getAttribute('name') || '';
        option.setAttribute('name', currentName.replace(/^properties\[/, 'ignore-properties['));
      });

      this.addToCartButton.classList.add('hidden');
      this.selectOptionsButton.classList.add('active');
      this.releaseAddToCartLock();
      this.setTermsRequired(false);

      if(this.stockEl) this.stockEl.style.display = 'flex';

      this.productForm.bundleId = null;
      this.productForm.bundleParentId = null;
      this.productForm.bundleDecrease = null;
      this.updatePrices(this.previousPrice, this.previousCompareAtPrice);

      this.customSelected = false;
    }

    /**
     * Updates all price display elements with new values.
     * @param {string} basePrice - The formatted base price string
     * @param {string} compareAtPrice - The formatted compare-at price string
     * @returns {void}
     */
    updatePrices(basePrice, compareAtPrice) {
      this.prices.forEach(price => price.textContent = basePrice);
      this.compareAtPrices.forEach(price =>  price.textContent = compareAtPrice);
    }

    /**
     * Collects merchant-defined custom option key/value pairs for line item properties.
     * @returns {Object<string, string>}
     */
    collectCustomOptionAttrs() {
      const customOptions = {};

      this.options.forEach((input) => {
        const match = input.getAttribute('name')?.match(/^(?:ignore-)?properties\[(.+)\]$/);
        if (!match) return;

        const propertyName = match[1];
        if (propertyName.startsWith('_')) return;
        if (input.value === '' || input.value == null) return;

        customOptions[propertyName] = String(input.value);
      });

      return customOptions;
    }

    /**
     * Formats and updates the displayed product prices for the current selection.
     * Bundle products use charge totals only; standard custom products include the base price.
     * @returns {void}
     */
    updateDisplayedPrices() {
      const rawAddonsPrice = this.addonsPrice;
      const priceAdjustment = this.getBundlePriceAdjustmentAmount();
      const baseAmount = this.isBundleProduct
        ? Math.max(rawAddonsPrice - priceAdjustment, 0)
        : this.productPrice + rawAddonsPrice;
      const compareAtAmount = this.isBundleProduct
        ? (priceAdjustment > 0 ? rawAddonsPrice : 0)
        : this.productCompareAtPrice + rawAddonsPrice;

      this.updatePrices(
        window.formatMoneyFn(baseAmount, window.moneyFormat),
        window.formatMoneyFn(compareAtAmount, window.moneyFormat)
      );
    }
  
    /**
     * Handles custom select click events.
     * Closes all other open custom selects when one is clicked.
     * @param {MouseEvent} event - The click event
     * @returns {void}
     */
    onCustomSelectClick(event) {
      let customSelect = event.target.closest('custom-select');
      this.customSelects.forEach(select => {
        if(customSelect != select){
          let details = select.querySelector('details');
          details && details.removeAttribute('open');
        }
      });
    }
  
    /**
     * Handles option change events from selects and checkboxes.
     * Updates selected values and triggers required option checks.
     * @param {Event} event - The change event from the input
     * @returns {void}
     */
    onOptionChange(event) {
      let targetInput = event.target,
      customContainer = targetInput.closest('custom-select, custom-checkboxes'),
      isCheckbox = customContainer.classList.contains('custom-checkboxes'),
      selectedValue = customContainer.querySelector('.custom-select-option.active');

      if(targetInput && customContainer){
        this.selectedValues = this.selectedValues.filter(value => value.label !== customContainer.dataset.label);

        if(isCheckbox){
          this.selectedValues.push(...customContainer.selectedOptions);
          this.checkRequiredOptions(customContainer.selectedOptions.length > 0 ? customContainer : null);
        } else {
          this.selectedValues.push({
            id: selectedValue.dataset.id,
            label: customContainer.dataset.label,
            value: targetInput.value,
            name: selectedValue.dataset.name || '',
            option: selectedValue.dataset.option || selectedValue.dataset.value,
            variantIds: selectedValue.dataset.variantIds?.split(',') || [],
            selectionType: customContainer.dataset.selectionType,
            price: selectedValue.dataset.price ? Number(selectedValue.dataset.price) : 0
          });

          this.checkRequiredOptions(customContainer);
        }  
      }
    }

    /**
     * Gets containers that need to be reset based on option dependencies.
     * @param {HTMLElement|null} customContainer - The container element that changed
     * @returns {HTMLElement[]} Array of container elements to reset
     */
    getContainersToReset(customContainer) {
      if (!customContainer) return [];
      
      const idsToCheck = Array.from(customContainer.querySelectorAll('.metaobject-custom-option-value')).map(option => option.dataset.id);
      
      if (idsToCheck.length === 0) return [];
      
      const allRequiredOptions = this.querySelectorAll('.metaobject-custom-option-value[data-required-options]');
      
      const containersToReset = [];
      allRequiredOptions.forEach(option => {
        const requiredIds = option.dataset.requiredOptions.split(',');
        if (requiredIds.some(id => idsToCheck.includes(id))) {
          containersToReset.push(option.closest('custom-select, custom-checkboxes'));
        }
      });
      
      return [...new Set(containersToReset)];
    }
    
    /**
     * Resets specified containers and removes their values from selection.
     * @param {HTMLElement[]} containers - Array of container elements to reset
     * @returns {void}
     */
    resetContainers(containers) {
      containers.forEach(container => {
        container.reset();
        this.selectedValues = this.selectedValues.filter(
          value => value.label !== container.dataset.label
        );

        // Bundle resets keep a selection; re-sync those values after the label filter
        if (container.selectedOptions?.length) {
          this.selectedValues.push(...container.selectedOptions);
        }
      });
    }

    /**
     * Updates visibility of options based on required dependencies.
     * Shows options that meet requirements and hides those that don't.
     * @returns {void}
     */
    updateOptionVisibility() {
      const allRequiredOptions = Array.from(
        this.querySelectorAll('.metaobject-custom-option-value[data-required-options]')
      );
      
      const selectedIds = this.selectedValues.map(value => value.id);
      const visibleOptions = this.getVisibleRequiredOptions(selectedIds);
      
      allRequiredOptions.forEach(option => {
        if (visibleOptions.includes(option)) {
          option.classList.remove('hidden-option');
        } else {
          this.hideAndResetOption(option);
        }
      });
    }

    /**
     * Gets array of options that should be visible based on selected IDs.
     * @param {string[]} selectedIds - Array of currently selected option IDs
     * @returns {HTMLElement[]} Array of option elements that should be visible
     */
    getVisibleRequiredOptions(selectedIds) {
      const visible = [];
      selectedIds.forEach(id => {
        const options = this.querySelectorAll(
          `.metaobject-custom-option-value[data-required-options*="${id}"]`
        );
        visible.push(...Array.from(options));
      });
      return visible;
    }
    
    /**
     * Hides an option and resets it if it's a checkbox.
     * @param {HTMLElement} option - The option element to hide and reset
     * @returns {void}
     */
    hideAndResetOption(option) {
      option.classList.add('hidden-option');
      if (option.classList.contains('custom-checkboxes-option')) {
        const checkboxInput = option.querySelector('.custom-checkbox-input');
        checkboxInput.checked = false;

        const checkboxGroup = option.closest('custom-checkboxes');
        if (checkboxGroup) {
          checkboxGroup.selectedOptions = checkboxGroup.selectedOptions.filter(
            value => value.id !== option.dataset.id
          );
          checkboxGroup.input.value = checkboxGroup.selectedOptions.map(value => value.value).join(',');
        }

        this.selectedValues = this.selectedValues.filter(
          value => value.id !== option.dataset.id
        );
      }
    }

    /**
     * Ensures the set_as_bundle option always has at least one available value selected.
     * Prefers merchant pre-selected (data-check) values when they are available.
     * @returns {void}
     */
    ensureBundleSelection() {
      if (!this.bundleOption) return;

      const checkedInput = this.bundleOption.querySelector('.custom-checkbox-input:checked');

      if (checkedInput) return;

      const defaultInputs = this.bundleOption.getPreferredDefaultInputs();

      defaultInputs.forEach(input => {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Returns selected values for the set_as_bundle option group.
     * @returns {SelectedValue[]}
     */
    getBundleSelectedValues() {
      if (!this.bundleOption) return [];

      return this.selectedValues.filter(value => value.label === this.bundleOption.dataset.label);
    }

    /**
     * Returns selected charge values that are not part of the set_as_bundle option.
     * @returns {SelectedValue[]}
     */
    getNonBundleSelectedValues() {
      return this.selectedValues.filter(value => {
        if (value.variantIds.length === 0) return false;
        if (!this.bundleOption) return true;
        return value.label !== this.bundleOption.dataset.label;
      });
    }

    /**
     * Returns how many bundle option values are currently selected.
     * Secondary charges are multiplied by this count.
     * @returns {number}
     */
    getBundleSelectionCount() {
      return Math.max(this.getBundleSelectedValues().length, 1);
    }

    /**
     * Returns ordered charge variant IDs with set_as_bundle variants first.
     * @returns {string[]}
     */
    getOrderedChargeVariantIds() {
      const bundleSelectedValues = this.getBundleSelectedValues().filter(value => value.variantIds.length > 0);
      const otherSelectedValues = this.getNonBundleSelectedValues();

      return [...bundleSelectedValues, ...otherSelectedValues].flatMap(value => value.variantIds);
    }

    /**
     * Normalizes adjustment required values into a sorted list of metaobject names.
     * @param {string[]|string|null|undefined} requiredValues
     * @returns {string[]}
     */
    normalizeRequiredValues(requiredValues) {
      const values = Array.isArray(requiredValues)
        ? requiredValues
        : String(requiredValues || '').split(',');

      return values
        .map(value => String(value || '').trim())
        .filter(Boolean)
        .sort();
    }

    /**
     * Parses a price decrease percentage value.
     * @param {number|string|null|undefined} priceDecrease
     * @returns {number}
     */
    parsePriceDecreasePercentage(priceDecrease) {
      if (priceDecrease == null || priceDecrease === '') return 0;

      const parsed = Number(priceDecrease);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    }

    /**
     * Finds the matching bundle price decrease percentage for the current selection.
     * Requires an exact match between required value names and selected bundle options.
     * @returns {number} Matching price decrease percentage, or 0
     */
    getBundlePriceDecreasePercentage() {
      if (!this.isBundleProduct || this.priceAdjustments.length === 0) return 0;

      const selectedNames = this.getBundleSelectedValues()
        .map(value => (value.name || '').trim())
        .filter(Boolean)
        .sort();

      const matchingAdjustment = this.priceAdjustments.find(adjustment => {
        const requiredValues = this.normalizeRequiredValues(adjustment.requiredValues);

        if (requiredValues.length === 0 || requiredValues.length !== selectedNames.length) {
          return false;
        }

        return requiredValues.every((value, index) => value === selectedNames[index]);
      });

      return matchingAdjustment
        ? this.parsePriceDecreasePercentage(matchingAdjustment.priceDecrease)
        : 0;
    }

    /**
     * Club-only discount in cents: defined % of set_as_bundle charges, not upcharges.
     * @returns {number}
     */
    getBundlePriceAdjustmentAmount() {
      const percentage = this.getBundlePriceDecreasePercentage();
      const clubPrice = this.getBundleSelectedValues().reduce((acc, value) => acc + value.price, 0);

      if (!percentage || !clubPrice) return 0;

      return Math.round(clubPrice * (percentage / 100));
    }

    /**
     * Bundle-wide % that equals the club-only discount when applied to the full total.
     * @param {number} totalPrice - Bundle total in cents
     * @param {number} discountAmount - Club-only discount in cents
     * @returns {number}
     */
    getEffectiveBundleDecreasePercentage(totalPrice, discountAmount) {
      if (!totalPrice || !discountAmount) return 0;

      return (discountAmount / totalPrice) * 100;
    }

    /**
     * Calculates the total charge price, multiplying non-bundle charges by bundle selection count.
     * @returns {number}
     */
    getCalculatedAddonsPrice() {
      if (!this.isBundleProduct) {
        return this.selectedValues.reduce((acc, value) => acc + value.price, 0);
      }

      const bundlePrice = this.getBundleSelectedValues().reduce((acc, value) => acc + value.price, 0);
      const nonBundlePrice = this.getNonBundleSelectedValues().reduce((acc, value) => acc + value.price, 0);

      return bundlePrice + (nonBundlePrice * this.getBundleSelectionCount());
    }

    /**
     * Updates product form with selected addon data.
     * Calculates total addon price and updates form properties.
     * For bundles, the first set_as_bundle charge replaces the main product id,
     * and non-bundle charges are multiplied by the bundle selection count.
     * @returns {void}
     */
    updateProductFormAddons() {
      this.addonsPrice = this.getCalculatedAddonsPrice();

      const priceAdjustment = this.getBundlePriceAdjustmentAmount();
      const decreasePercentage = this.getEffectiveBundleDecreasePercentage(this.addonsPrice, priceAdjustment);

      this.productForm.addonsPrice = Math.max(this.addonsPrice - priceAdjustment, 0);

      if (this.isBundleProduct && this.customSelected) {
        const bundleVariantIds = this.getBundleSelectedValues()
          .filter(value => value.variantIds.length > 0)
          .flatMap(value => value.variantIds).reverse();
        const nonBundleVariantIds = this.getNonBundleSelectedValues().flatMap(value => value.variantIds);
        const bundleSelectionCount = this.getBundleSelectionCount();
        const [primaryVariantId, ...remainingBundleVariantIds] = bundleVariantIds;

        if (primaryVariantId) {
          this.variantInput.value = primaryVariantId;
        }

        this.productForm.addons = [
          ...remainingBundleVariantIds.map(id => ({ id, quantityMultiplier: 1, isBundleClub: true })),
          ...nonBundleVariantIds.map(id => ({ id, quantityMultiplier: bundleSelectionCount })),
        ];
        this.productForm.bundleId = this.bundleId;
        this.productForm.bundleParentId = this.customOptionVariantId;
        this.productForm.bundleDecrease = decreasePercentage > 0 ? decreasePercentage : null;
        return;
      }

      this.productForm.addons = this.getOrderedChargeVariantIds();
      this.productForm.bundleId = null;
      this.productForm.bundleParentId = null;
      this.productForm.bundleDecrease = null;
    }
  
    /**
     * Checks and handles required option dependencies.
     * Main orchestration method for option validation and UI updates.
     * @param {HTMLElement|null} [customContainer=null] - The container that triggered the check
     * @returns {void}
     */
    checkRequiredOptions(customContainer = null) {
      const containersToReset = this.getContainersToReset(customContainer);

      this.resetContainers(containersToReset);
      this.updateOptionVisibility();

      if (this.isBundleProduct && this.getBundleSelectedValues().length === 0) {
        this.ensureBundleSelection();
        return;
      }

      this.updateProductFormAddons();

      if(this.customSelected){
        this.updateDisplayedPrices();
        this.checkAddToCartButtonVisibility();
      }
    }

    /**
     * Checks if all required options are selected and updates button visibility.
     * Shows add to cart button when all options selected, shows prompt otherwise.
     * @returns {void}
     */
    checkAddToCartButtonVisibility() {
      // Never gate the button while the Standard tab is active
      if (!this.isCustomTabActive()) {
        this.releaseAddToCartLock();
        return;
      }

      const complete = this.areCustomOptionsComplete();

      this.addToCartButton.classList.toggle('hidden', !complete);
      this.selectOptionsButton.classList.toggle('active', !complete);

      // Lock the button while incomplete. Only unlock what was locked here,
      // so a sold-out state set elsewhere is left alone.
      if (!complete) {
        this.addToCartButton.setAttribute('disabled', 'disabled');
        this.addToCartButton.dataset.customOptionsLocked = 'true';
      } else {
        this.releaseAddToCartLock();
      }
    }

    /**
     * Removes the lock set by checkAddToCartButtonVisibility, if any.
     * @returns {void}
     */
    releaseAddToCartLock() {
      if (!this.addToCartButton.dataset.customOptionsLocked) return;
      this.addToCartButton.removeAttribute('disabled');
      delete this.addToCartButton.dataset.customOptionsLocked;
    }

    /**
     * Whether the Custom options tab is the one currently selected.
     * Reads the tab itself so it can't drift out of step with the page.
     * @returns {boolean}
     */
    isCustomTabActive() {
      const customTab = this.productForm.querySelector('.custom-options-tab');
      return customTab ? customTab.checked : this.customSelected;
    }

    /**
     * The terms checkbox only applies to custom options.
     * Leaving it required while that tab is hidden blocks a standard add to basket.
     * @param {boolean} required
     * @returns {void}
     */
    setTermsRequired(required) {
      if (!this.acceptTermsInput) return;

      this.acceptTermsInput.required = required;
      this.acceptTermsInput.disabled = !required;

      if (!required) this.acceptTermsError?.classList.add('hidden');
    }

    /**
     * Whether an option has been chosen in every custom option field.
     * @returns {boolean}
     */
    areCustomOptionsComplete() {
      return new Set(this.selectedValues.map(value => value.label)).size === this.optionsCount;
    }
  }
  
  customElements.define('custom-options', CustomOptions);
  
  
  /**
   * Custom select dropdown web component.
   * Provides a styled select dropdown with image support.
   * @extends HTMLElement
   * 
   * @property {NodeListOf<HTMLElement>} options - All option elements in the select
   * @property {HTMLInputElement} input - Hidden input that stores the selected value
   * @property {HTMLDetailsElement} details - Details element that controls dropdown visibility
   * @property {HTMLElement} summary - Summary element that displays selected value
   * @property {HTMLElement} summaryText - Text element within summary
   * @property {string} summaryTextContent - Original placeholder text for the summary
   */
  class CustomSelect extends HTMLElement {
    constructor() {
      super();
  
      this.options = this.querySelectorAll('.custom-select-option');
      this.input = this.querySelector('input');
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.summaryText = this.querySelector('.custom-select-summary-text');
      this.summaryTextContent = this.summaryText.textContent;
  
      this.options.forEach(option => {
        option.addEventListener('click', this.onOptionClick.bind(this));
      });
  
    }
  
    /**
     * Resets the select to its initial state.
     * Clears selection, removes active states, and hides required options.
     * @returns {void}
     */
    reset() {
      this.options.forEach(option => {
        option.classList.remove('active');
        if(option.hasAttribute('data-required-options')){
          option.classList.add('hidden-option');
        }
      });
      this.input.value = '';
      this.summaryText.textContent = this.summaryTextContent;
      this.summaryText.style.removeProperty('--selected-image');
      this.summaryText.removeAttribute('data-selected-image');
      this.details.open = false;
    }
  
    /**
     * Closes the dropdown.
     * @returns {void}
     */
    close() {
      this.details.open = false;
    }
  
    /**
     * Opens the dropdown.
     * @returns {void}
     */
    open() {
      this.details.open = true;
    }
  
    /**
     * Handles option click events.
     * Updates selection, sets input value, and optionally displays option image.
     * @param {MouseEvent} event - The click event
     * @returns {void}
     */
    onOptionClick(event) {
      let option = event.target.closest('.custom-select-option'),
      selectedImage = option.dataset?.image;

      if(option.classList.contains('active')){
        this.close();
        return;
      }
      this.options.forEach(option => option.classList.remove('active'));
      option.classList.add('active');
      this.input.value = option.dataset.value;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
      this.summaryText.textContent = option.textContent;

      if(selectedImage){
        this.summaryText.style.setProperty('--selected-image', `url('${selectedImage}')`);
        this.summaryText.setAttribute('data-selected-image', "");
      } else {
        this.summaryText.style.removeProperty('--selected-image');
        this.summaryText.removeAttribute('data-selected-image');
      }

      this.details.open = false;
    }
  }
  
  customElements.define('custom-select', CustomSelect);
  
  
  /**
   * Global click handler to close open custom select dropdowns.
   * Closes any open custom-select when clicking outside of them.
   * @param {MouseEvent} e - The click event
   */
  document.addEventListener('click', e => {
    let closestSelect = e.target.closest('custom-select');
  
    if(!closestSelect){
      document.querySelectorAll('custom-select:has(details[open])').forEach(select => {
        select.close();
      });
    }
});

/**
 * Custom checkboxes group web component.
 * Manages a group of related checkbox options with multi-select capability.
 * @extends HTMLElement
 * 
 * @property {HTMLInputElement} input - Hidden input that stores comma-separated selected values
 * @property {NodeListOf<HTMLInputElement>} options - All checkbox input elements
 * @property {SelectedValue[]} selectedOptions - Array of currently selected checkbox options
 * @property {boolean} isBundleOption - Whether this group is the set_as_bundle option
 */
class CustomCheckboxes extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('.custom-checkboxes-input');
    this.options = this.querySelectorAll('.custom-checkboxes-option .custom-checkbox-input');
    this.isBundleOption = this.hasAttribute('data-set-as-bundle');

    this.selectedOptions = [];

    this.options.forEach(option => {
      option.addEventListener('change', this.onOptionChange.bind(this));
    });
  }

  /**
   * Returns available inputs that should be selected by default.
   * Prefers merchant pre-selected (data-check) values; otherwise the first available input.
   * @returns {HTMLInputElement[]}
   */
  getPreferredDefaultInputs() {
    const availableOptions = Array.from(this.querySelectorAll(
      '.custom-checkboxes-option:not([disabled]):not(.hidden-option)'
    ));
    const preselectedInputs = availableOptions
      .map(option => option.querySelector('.custom-checkbox-input[data-check]'))
      .filter(Boolean);

    if (preselectedInputs.length > 0) return preselectedInputs;

    const firstAvailableInput = availableOptions[0]?.querySelector('.custom-checkbox-input');

    return firstAvailableInput ? [firstAvailableInput] : [];
  }

  /**
   * Resets all checkboxes to unchecked state.
   * Bundle options restore available pre-selected values, or the first available value.
   * Hides required options and dispatches change events.
   * @returns {void}
   */
  reset() {
    this.options.forEach(option => {
      option.checked = false;
      if(option.closest('.custom-checkboxes-option')?.hasAttribute('data-required-options')){
        option.closest('.custom-checkboxes-option').classList.add('hidden-option');
      }
    });

    if (this.isBundleOption) {
      this.getPreferredDefaultInputs().forEach(input => {
        input.checked = true;
      });
    }

    this.options.forEach(option => {
      option.dispatchEvent(new Event('change'));
    });
  }

  /**
   * Handles checkbox change events.
   * Updates selected options array and hidden input value.
   * Bundle options cannot be fully deselected.
   * @param {Event} event - The change event from the checkbox
   * @returns {void}
   */
  onOptionChange(event) {
    let targetInput = event.target,
    checkboxOption = targetInput.closest('.custom-checkboxes-option');

    if (this.isBundleOption && !targetInput.checked) {
      const checkedCount = Array.from(this.options).filter(option => option.checked).length;

      if (checkedCount === 0) {
        targetInput.checked = true;
        return;
      }
    }

    this.selectedOptions = this.selectedOptions.filter(option => option.id !== checkboxOption.dataset.id);

    if(targetInput.checked){
      this.selectedOptions.push({
        id: checkboxOption.dataset.id,
        label: this.dataset.label,
        value: checkboxOption.dataset.value,
        name: checkboxOption.dataset.name || '',
        option: checkboxOption.dataset.option || checkboxOption.dataset.value,
        variantIds: checkboxOption.dataset.variantIds?.split(',') || [],
        selectionType: this.dataset.selectionType,
        price: checkboxOption.dataset.price ? Number(checkboxOption.dataset.price) : 0
      });
    }

    this.input.value = this.selectedOptions.map(option => option.value).join(',');

    this.input.dispatchEvent(new Event('change'));
  }
}

customElements.define('custom-checkboxes', CustomCheckboxes);
